import type { FC } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { routes } from "src/app/routes";
import { Color, FontSize, FontWeight } from "src/theme";
import { useTranslation } from "src/translations";
import { OutageDto, OutageType } from "src/types";
import { isNonNullish } from "src/utils";
import { OUTAGE_STATE_TEXT } from "../constants";
import { InfoRow } from "../InfoRow";
import { Flex } from "../Layouts";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";

type OutageAlertProps = Readonly<{
    outage: OutageDto;
}>;

type AlertProps = Readonly<{
    clickable?: boolean;
    type?: OutageType;
    color?: Color;
}>;

const TooltipContent: FC<{ outage: OutageDto }> = ({ outage }) => {
    const { t } = useTranslation();
    const { start, expectedEnd } = outage;

    return (
        <StyledTooltipWrapper flexDirection="column" gap="0.5rem">
            <Flex flexDirection="row" gap="1rem">
                <Alert type={outage.category} />
                <Text weight={FontWeight._700} size={FontSize._14}>
                    {outage.summary}
                </Text>
            </Flex>
            <Flex flexDirection="column" gap="0.25rem">
                <InfoRow title={t("outage.start")} data={start} />
                <InfoRow title={t("outage.expectedEnd")} data={expectedEnd} />
                <InfoRow title={t("outage.state")} data={t(OUTAGE_STATE_TEXT[outage.issueState])} />
                {isNonNullish(outage.subsystemsNames) && (
                    <Flex flexDirection="column">
                        <Text wordBreak="normal" size={FontSize._14}>
                            {t("outage.subSystems")}:
                        </Text>
                        <Text size={FontSize._14} weight={FontWeight._700}>
                            {outage.subsystemsNames.join(", ")}
                        </Text>
                    </Flex>
                )}
                <Flex flexDirection="column">
                    <Text wordBreak="normal" size={FontSize._14}>
                        {t("outage.title")}:
                    </Text>
                    <Text size={FontSize._14} weight={FontWeight._700}>
                        {outage.description}
                    </Text>
                </Flex>
            </Flex>
        </StyledTooltipWrapper>
    );
};

export const Alert: FC<AlertProps> = ({ clickable = false, type, color }) => (
    <OutageAlertWrapper clickable={clickable} type={type} color={color} />
);

export const OutageAlert: FC<OutageAlertProps> = ({ outage }) => (
    <Tooltip content={<TooltipContent outage={outage} />}>
        <Link to={routes.outageDetail.fillPathParams({ outageId: outage.id!.toString() })}>
            <Alert clickable type={outage.category} />
        </Link>
    </Tooltip>
);

const OutageAlertWrapper = styled.div<AlertProps>`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: ${({ theme }) => theme.borderRadius._5};
    background-color: ${({ theme, type, color }) => {
        if (color) {
            return color;
        }

        switch (type) {
            case OutageType.PlannedOutage:
                return theme.color.green;
            case OutageType.UnplannedOutage:
                return theme.color.red100;
            case OutageType.UnplannedPartialOutage:
                return theme.color.yellow100;
            default:
                return "unset";
        }
    }};
    cursor: ${({ clickable }) => (clickable ? "pointer" : "unset")};
`;

const StyledTooltipWrapper = styled(Flex)`
    padding: 0.5rem;
    width: 270px;
    white-space: normal;
    word-break: break-word;
`;
